/**
 * REGISTRO-CLIENTE — lado página del ciclo de vida del SW (SSOT de los 4 fronts).
 *
 * Reemplaza los 4 bloques de registro duplicados (cashier index.html, TV
 * main.js, customer main.js, promoter que directamente NO tenía — su SW
 * emitía RELOAD_REQUIRED al vacío). Es ES module normal del bundle de la app
 * (acá no aplica el piso WebView del kernel: esto lo transpila Vite como el
 * resto del front).
 *
 * Políticas de update (parámetro central — timing de negocio, ver SPEC):
 *  - 'reload-agresivo'   cashier/promoter: build nuevo detectado ⇒ reload YA
 *                        (con guard). Un operador no-técnico no puede quedar
 *                        atrapado en un build viejo.
 *  - 'proximo-arranque'  TV kiosko: el SW nuevo espera en `waiting`; cada
 *                        arranque limpio (load) lo activa — la TV rearranca
 *                        seguido, esa es la ventana natural. Jamás en caliente
 *                        (assets nuevos sobre HTML viejo rompen la sesión).
 *  - 'consultar'         customer: JAMÁS recargar solo — con fichas puestas o
 *                        rueda girando es inaceptable. Al load inicial (antes
 *                        de apostar) sí aplica un update pendiente; a mitad de
 *                        sesión avisa vía onNeedRefresh(aplicar) y el front
 *                        decide cuándo (o nunca: próxima apertura).
 *
 * Guard anti-loop (todas las políticas): tope de reintentos en sessionStorage
 * + gate navigator.onLine. Diseño validado en cashier (2026-07-10): una falla
 * persistente deja la app vieja pero USABLE, nunca pantalla parpadeante.
 */

const MAX_RELOADS_POR_SESION = 3;
const UPDATE_CADA_MS_DEFAULT = 5 * 60 * 1000;

/**
 * @param {Object} [opciones]
 * @param {'reload-agresivo'|'proximo-arranque'|'consultar'} [opciones.politicaUpdate='reload-agresivo']
 * @param {(aplicar: () => void) => void} [opciones.onNeedRefresh] - solo 'consultar':
 *   se llama cuando hay build nuevo a mitad de sesión; el front invoca `aplicar()`
 *   cuando sea seguro (sin apuesta en curso). Default: no hacer nada (se aplica
 *   solo en la próxima apertura).
 * @param {number} [opciones.updateCadaMs=300000] - polling de registration.update().
 * @param {string} [opciones.swUrl='/sw.js']
 * @param {string} [opciones.storageKeyGuard='sw_reload_required_count']
 */
export function registrarServiceWorker(opciones = {}) {
    if (!('serviceWorker' in navigator)) return;

    const politica = opciones.politicaUpdate || 'reload-agresivo';
    const updateCadaMs = opciones.updateCadaMs || UPDATE_CADA_MS_DEFAULT;
    const swUrl = opciones.swUrl || '/sw.js';
    const storageKey = opciones.storageKeyGuard || 'sw_reload_required_count';

    // Guard anti-loop compartido: sin red, recargar reintenta el mismo fetch
    // fallido → mismo estado → loop; se espera el próximo 'online'. Con red
    // pero falla persistente (ej. server mal configurado), el tope corta el
    // parpadeo infinito. sessionStorage: se limpia solo al cerrar la pestaña.
    function recargarConGuard() {
        const intentos = parseInt(sessionStorage.getItem(storageKey) || '0', 10);
        if (intentos >= MAX_RELOADS_POR_SESION) return;
        function recargar() {
            sessionStorage.setItem(storageKey, String(intentos + 1));
            window.location.reload();
        }
        if (navigator.onLine) {
            recargar();
        } else {
            window.addEventListener('online', recargar, { once: true });
        }
    }

    function pedirActivacion(worker) {
        if (worker) worker.postMessage({ type: 'SKIP_WAITING' });
    }

    navigator.serviceWorker
        .register(swUrl)
        .then(registration => {
            // Update pendiente de una sesión anterior (SW nuevo ya descargado,
            // esperando). El load es la ventana segura de TODAS las políticas:
            // arranque limpio (TV) / antes de apostar (customer) / siempre
            // (agresivo). Las políticas 'deferred' lo activan explícitamente;
            // en 'immediate' el propio SW ya hizo skipWaiting.
            if (registration.waiting && politica !== 'reload-agresivo') {
                pedirActivacion(registration.waiting);
            }

            // Build nuevo detectado DURANTE la sesión. `controller` preexistente
            // distingue update real de la instalación inicial (que no lo tiene).
            registration.addEventListener('updatefound', () => {
                const nuevoWorker = registration.installing;
                if (!nuevoWorker) return;
                nuevoWorker.addEventListener('statechange', () => {
                    if (nuevoWorker.state !== 'installed' || !navigator.serviceWorker.controller) return;
                    if (politica === 'reload-agresivo') {
                        // El SW (activation:'immediate') ya se activó solo — el
                        // reload hace que ESTA pestaña tome el build nuevo.
                        recargarConGuard();
                    } else if (politica === 'consultar' && opciones.onNeedRefresh) {
                        opciones.onNeedRefresh(() => pedirActivacion(registration.waiting));
                    }
                    // 'proximo-arranque': nada en caliente — el próximo load lo toma.
                });
            });

            // registration.update() revisa si /sw.js cambió en el server (no-op
            // si no). Inmediato al cargar (cubre pestañas abiertas desde antes
            // de un deploy), al reconectar (caja que estuvo offline) y polling.
            registration.update().catch(() => {});
            window.addEventListener('online', () => {
                registration.update().catch(() => {});
            });
            setInterval(() => {
                registration.update().catch(() => {});
            }, updateCadaMs);
        })
        .catch(() => {});

    // El SW avisa que un asset pidió un bundle que ya no existe (build viejo).
    navigator.serviceWorker.addEventListener('message', e => {
        if (e.data && e.data.type === 'RELOAD_REQUIRED') recargarConGuard();
    });

    // Políticas diferidas: cuando el SW nuevo finalmente toma control (tras
    // pedirActivacion), la página sigue corriendo el JS viejo — un reload la
    // alinea. En 'consultar' esto solo dispara después de que el front llamó
    // `aplicar()` (o en el load siguiente), nunca en medio de una apuesta.
    if (politica !== 'reload-agresivo') {
        let yaRecargo = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (yaRecargo) return;
            yaRecargo = true;
            recargarConGuard();
        });
    }
}
